import React, { useState } from "react";
import { Card, Form, Button, Row, Col, InputGroup } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEye,
  faEyeSlash,
} from "@fortawesome/free-solid-svg-icons";
import useSecurityQuestions from "hooks/accountManagement/useSecurityQuestions";
import AsyncSelect from "react-select/async";
import selectStyles from "components/olitask/donations/credits/selectStyles";
import apiClient from "utils/apiClient";
import { toast } from "react-toastify";

export default function SecurityQuestionsForm({ onBack }) {
  const { securityQns, loading: loadingQns } = useSecurityQuestions();

  const [questions, setQuestions] = useState([]);
  const [showAnswers, setShowAnswers] = useState([false]);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const getFilteredOptions = (currentIndex) => {
    const selectedIds = questions
      .map((q, i) => (i === currentIndex ? null : q.question?.value))
      .filter(Boolean);

    return securityQns
      .filter((q) => !selectedIds.includes(q.id))
      .map((q) => ({
        value: q.id,
        label: q.questionText,
      }));
  };

  const handleChange = (index, field, value) => {
    const newQuestions = [...questions];
    newQuestions[index][field] = value;
    setQuestions(newQuestions);
  };

  const handleAdd = () => {
    if (questions.length < 3) {
      setQuestions([...questions, { question: "", answer: "" }]);
      setShowAnswers([...showAnswers, false]);
    } else {
      toast.error("You can only set exactly 3 questions.");
    }
  };

  const handleRemove = (index) => {
    if (questions.length > 0) {
      setQuestions(questions.filter((_, i) => i !== index));
      setShowAnswers(showAnswers.filter((_, i) => i !== index));
    }
  };

  const toggleShowAnswer = (index) => {
    const newShow = [...showAnswers];
    newShow[index] = !newShow[index];
    setShowAnswers(newShow);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (questions.length < 1 || questions.length > 3) {
      toast.error("Please select between 1 and 3 security questions.");
      return;
    }

    if (!password) {
      toast.error("Please enter your password to continue.");
      return;
    }

    const payload = {
      question1Id: questions[0]?.question?.value,
      answer1: questions[0]?.answer,
      question2Id: questions[1]?.question?.value,
      answer2: questions[1]?.answer,
      question3Id: questions[2]?.question?.value,
      answer3: questions[2]?.answer,
      password: password,
    };

    try {
      await apiClient.put(
        "/identity-service/api/v1/auth/security-questions",
        payload
      );
      toast.success("Security questions saved successfully!");
    } catch (err) {
      // err.response.data might contain the error object depending on your axios setup
      const errorData = err?.response?.data || err;

      if (errorData.errors && typeof errorData.errors === "object") {
        // Join all error messages into a string
        const messages = Object.entries(errorData.errors)
          .map(([field, msg]) => `${field}: ${msg}`)
          .join("\n");

        toast.error(`Validation failed:\n${messages}`);
      } else if (errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to save security questions");
      }
    }
  };

  return (
    <Row>
      <Col xs={12} lg={12}>
        <Button
          variant="light"
          size="sm"
          className="border text-xxs mb-3"
          onClick={onBack}
        >
          <FontAwesomeIcon
            icon="chevron-left"
            className="me-2 text-secondary"
          />
          Back
        </Button>

        <h6 className="mb-0 fw-semibold text-sm mt-2">
          Set Up Security Questions
        </h6>
        <p className="mb-3 text-xxs">
          Add security questions to enhance identity verification if you ever
          need to reset your password.
        </p>

        {questions.length > 0 ? (
          <Form onSubmit={handleSubmit} autoComplete="off">
            {questions.map((q, idx) => (
              <div key={idx} className="mb-4">
                <Form.Label className="text-sm fw-semibold">
                  Question {idx + 1}
                </Form.Label>

                <AsyncSelect
                  cacheOptions
                  defaultOptions={getFilteredOptions(idx)}
                  loadOptions={(inputValue) => {
                    const filtered = getFilteredOptions(idx).filter((opt) =>
                      opt.label.toLowerCase().includes(inputValue.toLowerCase())
                    );
                    return Promise.resolve(filtered);
                  }}
                  onChange={(opt) => handleChange(idx, "question", opt)}
                  value={q.question}
                  placeholder="Search and select question..."
                  isClearable
                  isLoading={loadingQns}
                  styles={selectStyles}
                  autoComplete="off"
                />

                <Form.Label className="text-sm form-label mt-3">
                  Answer
                </Form.Label>
                <InputGroup className="mb-2">
                  <Form.Control
                    type={showAnswers[idx] ? "text" : "password"}
                    placeholder="Enter your answer"
                    value={q.answer}
                    onChange={(e) =>
                      handleChange(idx, "answer", e.target.value)
                    }
                    autoComplete="new-password"
                  />
                  <Button
                    variant="light border"
                    onClick={() => toggleShowAnswer(idx)}
                  >
                    <FontAwesomeIcon
                      icon={showAnswers[idx] ? faEyeSlash : faEye}
                    />
                  </Button>
                </InputGroup>

                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleRemove(idx)}
                  className="text-xxs"
                >
                  Remove
                </Button>
              </div>
            ))}

            {questions.length < 3 && (
              <Button
                variant="light"
                size="sm"
                onClick={handleAdd}
                className="mb-3"
              >
                + Add another question
              </Button>
            )}

            {/* PASSWORD INPUT */}
            <Form.Group className="mb-3">
              <Form.Label className="text-sm">Confirm Password</Form.Label>
              <InputGroup>
                <Form.Control
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
                <Button
                  variant="light border"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </Button>
              </InputGroup>
            </Form.Group>

            <Button variant="success" type="submit" className="w-100 text-sm">
              Save Security Questions
            </Button>
          </Form>
        ) : (
          <div className="text-center py-4 px-3 border rounded bg-light">
            <FontAwesomeIcon icon="shield-alt" className="text-muted mb-2" />
            <h6 className="fw-semibold text-muted mb-1">
              No security questions have been set
            </h6>
            <Button
              variant="light"
              size="sm"
              onClick={handleAdd}
              className="mt-2 border"
            >
              + Add question
            </Button>
          </div>
        )}
      </Col>
    </Row>
  );
}
